import { Icon } from 'components/ui/Icon'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import clsx from 'clsx'

type FileRowProps = {
  fileName: string
  fileSize?: number
  /** Image thumbnail shown instead of the file-type icon (e.g. composer previews). */
  previewUrl?: string
  onDelete?: () => void
  onClick?: () => void
  /** True when rendered on the user's primary-colored side (chat messages aligned right). */
  dark?: boolean
}

const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || ''
}

const getFileIcon = (filename: string): IconName => {
  const ext = getFileExtension(filename)
  const iconMap: Record<string, IconName> = {
    pdf: 'file-pdf',
    xls: 'file-excel',
    xlsx: 'file-excel',
    csv: 'file-excel',
    doc: 'file-word',
    docx: 'file-word',
    ppt: 'file-powerpoint',
    pptx: 'file-powerpoint',
    zip: 'file-archive',
    rar: 'file-archive',
    jpg: 'file-image',
    jpeg: 'file-image',
    png: 'file-image',
    gif: 'file-image',
    webp: 'file-image',
  }
  return iconMap[ext] || 'file'
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const FileRow = ({ fileName, fileSize, previewUrl, onDelete, onClick, dark = false }: FileRowProps) => {
  return (
    <div
      className={clsx(
        'tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-p-2 tw-pr-2.5',
        onClick && 'tw-cursor-pointer tw-transition-opacity hover:tw-opacity-80',
      )}
      style={
        dark
          ? { background: 'linear-gradient(167deg, var(--primary-gradient-start), var(--primary-gradient-end))' }
          : { backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-default)' }
      }
      onClick={onClick}
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={fileName}
          className="tw-h-10 tw-w-10 tw-flex-shrink-0 tw-rounded-lg tw-object-cover"
        />
      ) : (
        <div
          className="tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg"
          style={{
            backgroundColor: dark
              ? 'rgba(255, 255, 255, 0.2)'
              : 'color-mix(in srgb, var(--brand-text) 12%, transparent)',
          }}
        >
          <Icon
            icon={['fas', getFileIcon(fileName)]}
            className="tw-h-4 tw-w-4"
            style={{ color: dark ? '#ffffff' : 'var(--brand-text)' }}
          />
        </div>
      )}
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
        <span
          className="tw-truncate tw-text-xs tw-font-medium"
          style={{ color: dark ? '#ffffff' : 'var(--text-primary)' }}
        >
          {fileName}
        </span>
        {fileSize !== undefined && (
          <span className="tw-text-xs" style={{ color: dark ? 'rgba(255, 255, 255, 0.7)' : 'var(--text-tertiary)' }}>
            {formatFileSize(fileSize)}
          </span>
        )}
      </div>
      {onDelete && (
        <button
          type="button"
          aria-label={fileName}
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          className={clsx(
            'tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-transition-colors',
            dark ? 'hover:tw-bg-surface/20' : 'hover:tw-bg-[var(--border-default)]',
          )}
        >
          <Icon
            icon={['far', 'times']}
            className="tw-h-3 tw-w-3"
            style={{ color: dark ? 'rgba(255, 255, 255, 0.8)' : 'var(--text-tertiary)' }}
          />
        </button>
      )}
    </div>
  )
}
